import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  Alert,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab
} from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useForm990Data } from '../../../hooks/useForm990Data';
import { PreviewDisplay } from './components/Preview/PreviewDisplay';
import { SignaturePanel } from './components/SignaturePanel';
import { PartIVQuestionnaire } from './components/PartIVQuestionnaire';
import { useForm990Context } from '../../../contexts/Form990Context';
import { api, form990API } from '../../../services/api';
import styled from '@emotion/styled';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../../../../contexts/AuthContext';
import { Form990Data } from '../../../types/reporting';

interface Form990GeneratorProps {
  onClose: () => void;
}

// Define prop types for styled components
interface StyledStepLabelProps {
  active?: boolean;
  completed?: boolean;
  needsReview?: boolean;
}

interface CustomStepIconProps {
  completed?: boolean;
  active?: boolean;
  needsReview?: boolean;
}

// Extend Form990Data with the missing properties
interface ExtendedForm990Data extends Form990Data {
  validation?: any[];
  id?: string;
}

const steps = [
  'Questionnaire',
  'Preview & Review',
  'Sign & Submit'
];

const REVIEW_PERIOD_DAYS = 90;

const StyledStepLabel = styled(StepLabel)<StyledStepLabelProps>(({ active, completed, needsReview }) => ({
  '& .MuiStepLabel-label': {
    color: active ? '#2B82FE' : completed ? '#34d399' : needsReview ? '#f59e0b' : '#FFFFFF',
    cursor: 'pointer',
  },
  '& .MuiStepIcon-root': {
    color: active ? '#2B82FE' : completed ? '#34d399' : needsReview ? '#f59e0b' : 'rgba(255, 255, 255, 0.5)',
    cursor: 'pointer',
  },
  '& .MuiStepIcon-text': {
    fill: '#FFFFFF',
  },
  '& .MuiStepLabel-iconContainer': {
    '& .MuiSvgIcon-root': {
      width: '2em',
      height: '2em',
    }
  }
}));

const CustomStepIcon = ({ completed, active, needsReview }: CustomStepIconProps) => {
  if (completed) {
    return <CheckIcon sx={{ color: '#34d399' }} />;
  }
  if (needsReview) {
    return <WarningIcon sx={{ color: '#f59e0b' }} />;
  }
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: active ? '#2B82FE' : 'rgba(255, 255, 255, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
      }}
    >
      {active ? '•' : ''}
    </div>
  );
};

export const Form990Generator: React.FC<Form990GeneratorProps> = ({ onClose }) => {
  const { user } = useAuth();
  const fiscalSponsorId = user?.fiscalSponsorId || '';
  
  const { data, loading, error, regenerate990 } = useForm990Data(fiscalSponsorId);
  
  const [activeStep, setActiveStep] = useState(0);
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const [responses, setResponses] = useState<any>(null);
  const [showWarning, setShowWarning] = useState(false);

  const handleQuestionnaireComplete = async (data: any) => {
    setResponses(data);
    setQuestionnaireOpen(false);
    handleStepChange(1); // Move to Preview & Review step
  };

  const handleStepChange = (step: number) => {
    if (step === 0) {
      setQuestionnaireOpen(true);
    } else if (step === 1) {
      if (responses || data) {
        setActiveStep(1);
      } else {
        setShowWarning(true);
      }
    } else {
      setActiveStep(step);
    }
  };

  useEffect(() => {
    // Debug logging
    console.log('=== Form 990 Debug Info ===');
    
    // Log component props and state
    console.log('Component Location:', {
      file: 'src/sponsfi/components/Reports/Form990/index.tsx',
      route: window.location.pathname
    });

    // Log API endpoints being used
    console.log('API Endpoints:', {
      questionnaire: `${process.env.REACT_APP_API_URL}/api/sponsfi/form990/questionnaire`,
      generate: `${process.env.REACT_APP_API_URL}/api/sponsfi/form990/generate`,
      summary: `${process.env.REACT_APP_API_URL}/api/sponsfi/form990/summary`
    });

    // Log related files
    console.log('Related Files:', {
      components: [
        'src/sponsfi/components/Reports/Form990/index.tsx',
        'src/sponsfi/components/Reports/Form990/components/PartIVQuestionnaire.tsx'
      ],
      routes: [
        'src/sponsfi/api/routes/form990.routes.ts',
        'server/routes/fiscalSponsor.js'
      ],
      controllers: [
        'src/sponsfi/api/controllers/Form990Controller.ts',
        'src/sponsfi/api/controllers/reporting.controller.ts'
      ],
      services: [
        'src/sponsfi/services/Form990Service.ts',
        'src/sponsfi/services/ReportingEngine.ts'
      ]
    });

    // Test API endpoints
    const testEndpoints = async () => {
      try {
        const endpoints = [
          '/api/sponsfi/form990/debug',
          '/api/sponsfi/form990/questionnaire',
          '/api/sponsfi/form990/summary',
          '/api/debug'
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`);
            const data = await response.json();
            console.log(`Endpoint ${endpoint}:`, {
              status: response.status,
              data
            });
          } catch (error: any) {
            console.log(`Endpoint ${endpoint}:`, {
              error: error.message
            });
          }
        }
      } catch (error) {
        console.error('Error testing endpoints:', error);
      }
    };

    testEndpoints();
  }, []);

  const fetchDraft = async () => {
    try {
      const data = await form990API.getDrafts(); // Changed from getDraft to getDrafts
      // ... handle response
    } catch (error) {
      console.error('Error fetching draft:', error);
    }
  };

  const form990Data = data as ExtendedForm990Data;
  const isLoading = loading;

  if (!form990Data) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3, position: 'relative' }}>
      {onClose && (
        <IconButton 
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <Typography variant="h4" gutterBottom sx={{ color: '#FFFFFF' }}>
        Form 990 Generator
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {showWarning && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowWarning(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          Please complete the questionnaire or manually fill out all required fields before submitting.
        </Alert>
      )}

      <Box sx={{ width: '100%', mb: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StyledStepLabel 
                onClick={() => handleStepChange(index)}
                sx={{ cursor: 'pointer' }}
                active={activeStep === index}
              >
                {label}
              </StyledStepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={() => setQuestionnaireOpen(true)}
          >
            Open Form 990 Questionnaire
          </Button>
        )}

        {activeStep === 1 && (
          <PreviewDisplay
            data={form990Data as any}
            isLoading={isLoading}
            onNext={() => handleStepChange(2)}
            validation={form990Data?.validation || []}
            onUpdate={regenerate990}
          />
        )}

        {activeStep === 2 && (
          <SignaturePanel
            data={form990Data as any}
            onSign={async () => {/* Implement signature logic */}}
            onSubmit={async () => {/* Implement submit logic */}}
          />
        )}

        <PartIVQuestionnaire
          {...{
            questionnaireOpen,
            onClose: () => setQuestionnaireOpen(false),
            onSubmit: handleQuestionnaireComplete,
            initialData: responses,
            orgId: form990Data?.id
          } as any}
        />
      </Box>
    </Box>
  );
}; 